import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  MessageSquare, Send, CheckCircle2, XCircle, Loader2, Shield, User,
  ExternalLink, Sparkles, ArrowLeft,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import { RARITIES, VARIANTS } from '../mock';

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

const STATUS_PILL = {
  pending:  { cls: 'bg-yellow-300 text-[#3a1064]',  label: 'Pending' },
  accepted: { cls: 'bg-emerald-500 text-white',      label: 'Accepted' },
  declined: { cls: 'bg-rose-500 text-white',          label: 'Declined' },
};

export default function OfferChatDialog({
  open, onOpenChange, offer, onOfferChange,
}) {
  const { user, isAdmin } = useAuth();
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [actMode, setActMode] = useState(null); // 'accept' | 'decline' | null
  const [paymentLink, setPaymentLink] = useState('');
  const [meetUpNote, setMeetUpNote] = useState('');
  const [adminReply, setAdminReply] = useState('');
  const [acting, setActing] = useState(false);
  const listRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (listRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    });
  }, []);

  const fetchMessages = useCallback(async () => {
    if (!offer?.id) return;
    try {
      const list = await api.listMessages(offer.id);
      setMessages(list);
      scrollToBottom();
    } catch (e) {
      if (loading) toast({ title: 'Failed to load chat', description: e.message });
    }
  }, [offer?.id, loading, scrollToBottom]);

  // Initial load + polling while open
  useEffect(() => {
    if (!open || !offer?.id) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const list = await api.listMessages(offer.id);
        if (mounted) {
          setMessages(list);
          scrollToBottom();
        }
      } catch (e) {
        if (mounted) toast({ title: 'Failed to load chat', description: e.message });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    const t = setInterval(fetchMessages, 5000);
    return () => { mounted = false; clearInterval(t); };
  }, [open, offer?.id, fetchMessages, scrollToBottom]);

  // Reset transient state when dialog closes
  useEffect(() => {
    if (!open) {
      setBody(''); setActMode(null);
      setPaymentLink(''); setMeetUpNote(''); setAdminReply('');
      setActing(false);
    }
  }, [open]);

  const send = async () => {
    const txt = body.trim();
    if (!txt) return;
    setSending(true);
    // Optimistic
    const optimistic = {
      id: `temp-${Date.now()}`,
      offer_id: offer.id,
      sender_id: user.id,
      sender_role: isAdmin ? 'admin' : 'buyer',
      sender_name: user.name || user.email,
      body: txt,
      kind: 'text',
      created_at: new Date().toISOString(),
      _pending: true,
    };
    setMessages((prev) => [...prev, optimistic]);
    setBody('');
    scrollToBottom();
    try {
      const saved = await api.sendMessage(offer.id, txt);
      setMessages((prev) => prev.map((m) => (m.id === optimistic.id ? saved : m)));
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      toast({ title: 'Send failed', description: e.message });
      setBody(txt);
    } finally {
      setSending(false);
    }
  };

  const submitAct = async () => {
    if (!actMode) return;
    setActing(true);
    try {
      let updated;
      if (actMode === 'accept') {
        if (!paymentLink.trim()) {
          toast({ title: 'Payment link required' });
          setActing(false);
          return;
        }
        updated = await api.acceptOffer(offer.id, {
          payment_link: paymentLink.trim(),
          meet_up_note: meetUpNote.trim(),
          admin_reply: adminReply.trim(),
        });
        toast({ title: 'Offer accepted', description: 'Buyer has been notified.' });
      } else {
        updated = await api.declineOffer(offer.id, {
          admin_reply: adminReply.trim() || 'The admin declined this offer.',
        });
        toast({ title: 'Offer declined', description: 'Buyer has been notified.' });
      }
      onOfferChange?.(updated);
      setActMode(null);
      setPaymentLink(''); setMeetUpNote(''); setAdminReply('');
      await fetchMessages();
    } catch (e) {
      toast({ title: 'Action failed', description: e.message });
    } finally {
      setActing(false);
    }
  };

  const pill = STATUS_PILL[offer?.status] || STATUS_PILL.pending;
  const r = offer ? RARITIES[offer.sprite_rarity] : null;
  const v = offer ? VARIANTS[offer.sprite_variant] : null;
  const partnerLabel = isAdmin
    ? `with ${offer?.buyer_name || offer?.buyer_email || 'buyer'}`
    : 'with the Sprite Vault devs';

  // Group conditionals
  const showComposer = offer && (offer.status === 'pending' || isAdmin || true);
  // We allow chat even after accepted/declined so they can coordinate meet-up

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="offer-chat-dialog"
        className="bg-[#fff7d6] border-[3px] border-[#3a1064] rounded-2xl shadow-[0_8px_0_#3a1064] max-w-2xl p-0 overflow-hidden h-[88vh] sm:h-[80vh] flex flex-col"
      >
        {!offer ? null : (
          <>
            {/* Header */}
            <div
              className="border-b-[3px] border-[#3a1064] px-4 sm:px-5 py-3"
              style={{ background: 'linear-gradient(135deg,#ffd900 0%,#ff2bd6 100%)' }}
            >
              <DialogHeader className="text-left space-y-1">
                <DialogTitle className="text-[#3a1064] font-black text-lg sm:text-xl flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" /> Chat {partnerLabel}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Live chat about offer {offer.id}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-2 flex items-center gap-3">
                <img src={offer.sprite_img} alt="" className="h-12 w-12 object-contain bg-white rounded-lg border-2 border-[#3a1064] p-1 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-black text-[#3a1064] text-sm truncate">{offer.sprite_name}</div>
                  <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                    {r && <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded text-white" style={{ background: r.color }}>{r.label}</span>}
                    {v && <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded text-white" style={{ background: v.color }}>{v.label}</span>}
                    <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${pill.cls}`}>{pill.label}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-black text-[#3a1064] text-lg leading-none">${offer.offer_amount.toFixed(2)}</div>
                  <div className="text-[9px] font-black uppercase text-[#3a1064]/70">Offer</div>
                </div>
              </div>
            </div>

            {/* Action prompt (admin only, pending) */}
            {isAdmin && offer.status === 'pending' && !actMode && (
              <div className="px-4 sm:px-5 py-2 bg-yellow-100 border-b-2 border-[#ffd900] flex items-center gap-2 flex-wrap">
                <Sparkles className="h-4 w-4 text-[#ff2bd6]" />
                <span className="text-xs font-black text-[#3a1064] flex-1 min-w-[140px]">
                  Quick actions:
                </span>
                <Button
                  data-testid="chat-accept-btn"
                  onClick={() => setActMode('accept')}
                  className="h-8 px-3 rounded-lg border-2 border-[#3a1064] bg-emerald-500 hover:bg-emerald-600 text-white font-black text-xs shadow-[0_2px_0_#3a1064]"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Accept
                </Button>
                <Button
                  data-testid="chat-decline-btn"
                  variant="outline"
                  onClick={() => setActMode('decline')}
                  className="h-8 px-3 rounded-lg border-2 border-[#3a1064] bg-white hover:bg-rose-50 text-rose-700 font-black text-xs"
                >
                  <XCircle className="h-3.5 w-3.5 mr-1" /> Decline
                </Button>
              </div>
            )}

            {/* Accept/decline form (admin only) */}
            {isAdmin && actMode && (
              <div
                data-testid="chat-act-form"
                className={`px-4 sm:px-5 py-3 border-b-2 ${
                  actMode === 'accept' ? 'bg-emerald-50 border-emerald-300' : 'bg-rose-50 border-rose-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="font-black text-[#3a1064] text-sm flex items-center gap-1.5">
                    {actMode === 'accept' ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-rose-600" />}
                    {actMode === 'accept' ? 'Accept this offer' : 'Decline this offer'}
                  </div>
                  <button
                    onClick={() => setActMode(null)}
                    className="text-xs font-black text-[#3a1064] underline underline-offset-2 hover:text-[#ff2bd6] flex items-center gap-1"
                  >
                    <ArrowLeft className="h-3 w-3" /> Back to chat
                  </button>
                </div>
                {actMode === 'accept' && (
                  <>
                    <Label className="text-[#3a1064] font-black text-xs">Payment link <span className="text-rose-600">*</span></Label>
                    <Input
                      data-testid="chat-payment-link-input"
                      value={paymentLink}
                      onChange={(e) => setPaymentLink(e.target.value)}
                      placeholder="https://paypal.me/devs/7.50"
                      className="h-10 rounded-lg border-2 border-[#3a1064] font-bold text-[#3a1064] mt-1"
                    />
                    <Label className="text-[#3a1064] font-black text-xs mt-2 block">Meet-up details</Label>
                    <Textarea
                      data-testid="chat-meetup-input"
                      rows={2}
                      value={meetUpNote}
                      onChange={(e) => setMeetUpNote(e.target.value)}
                      placeholder="Tilted Towers · NA-East · 9pm EST"
                      className="rounded-lg border-2 border-[#3a1064] font-bold text-[#3a1064] resize-none mt-1"
                    />
                  </>
                )}
                <Label className="text-[#3a1064] font-black text-xs mt-2 block">Reply note <span className="text-[#3a1064]/50 font-bold">(optional)</span></Label>
                <Textarea
                  data-testid="chat-reply-input"
                  rows={2}
                  value={adminReply}
                  onChange={(e) => setAdminReply(e.target.value)}
                  placeholder={actMode === 'accept' ? 'Thanks! See you in-game.' : 'Sorry, reserved already.'}
                  className="rounded-lg border-2 border-[#3a1064] font-bold text-[#3a1064] resize-none mt-1"
                />
                <Button
                  data-testid="chat-act-submit-btn"
                  onClick={submitAct}
                  disabled={acting}
                  className={`w-full mt-3 h-10 rounded-lg border-2 border-[#3a1064] text-white font-black shadow-[0_3px_0_#3a1064] ${
                    actMode === 'accept' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-rose-500 hover:bg-rose-600'
                  }`}
                >
                  {acting ? <Loader2 className="h-4 w-4 animate-spin" /> :
                    actMode === 'accept' ? 'Accept & notify buyer' : 'Decline & notify buyer'}
                </Button>
              </div>
            )}

            {/* Buyer payment info banner */}
            {!isAdmin && offer.status === 'accepted' && offer.payment_link && (
              <div className="px-4 sm:px-5 py-3 bg-emerald-50 border-b-2 border-emerald-300 flex items-center gap-3 flex-wrap">
                <a
                  data-testid="chat-payment-link"
                  href={offer.payment_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white font-black text-xs px-3 py-2 rounded-lg border-2 border-[#3a1064] shadow-[0_2px_0_#3a1064]"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Pay now
                </a>
                {offer.meet_up_note && (
                  <div className="text-xs font-bold text-[#3a1064] flex-1">
                    <span className="font-black uppercase text-[9px] tracking-wider">Meet-up:</span> {offer.meet_up_note}
                  </div>
                )}
              </div>
            )}

            {/* Messages list */}
            <div
              ref={listRef}
              data-testid="chat-messages"
              className="flex-1 overflow-y-auto px-3 sm:px-4 py-3 space-y-2 bg-yellow-50/40"
            >
              {loading && messages.length === 0 ? (
                <div className="py-10 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin text-[#9d3dff]" /></div>
              ) : messages.length === 0 ? (
                <div className="py-10 text-center text-[#3a1064]/60 font-bold text-sm">No messages yet.</div>
              ) : (
                messages.map((m) => <ChatBubble key={m.id} msg={m} isMine={m.sender_id === user?.id} isAdmin={isAdmin} />)
              )}
            </div>

            {/* Composer */}
            {showComposer && (
              <div className="border-t-[3px] border-[#3a1064] bg-white px-3 sm:px-4 py-3">
                <form
                  className="flex items-end gap-2"
                  onSubmit={(e) => { e.preventDefault(); send(); }}
                >
                  <Textarea
                    data-testid="chat-input"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    rows={1}
                    placeholder={isAdmin ? `Reply to ${offer.buyer_name || offer.buyer_email}…` : 'Message the devs…'}
                    className="flex-1 min-h-[44px] max-h-[120px] rounded-xl border-2 border-[#3a1064] font-bold text-[#3a1064] resize-none"
                    maxLength={2000}
                  />
                  <Button
                    data-testid="chat-send-btn"
                    type="submit"
                    disabled={sending || !body.trim()}
                    className="h-11 px-3 sm:px-4 rounded-xl border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black shadow-[0_3px_0_#3a1064]"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </form>
                <div className="text-[10px] font-bold text-[#3a1064]/50 mt-1 px-1">
                  Enter to send · Shift+Enter for new line
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ChatBubble({ msg, isMine, isAdmin }) {
  if (msg.kind === 'system' || msg.sender_role === 'system') {
    return (
      <div className="flex justify-center my-2">
        <div className="bg-yellow-100 border-2 border-[#ffd900] rounded-full px-3 py-1 text-[11px] font-black text-[#3a1064] max-w-full break-words text-center">
          {msg.body}
        </div>
      </div>
    );
  }
  const side = isMine ? 'justify-end' : 'justify-start';
  const bubble = isMine
    ? 'bg-[#ff2bd6] text-white border-[#3a1064] shadow-[0_2px_0_#3a1064] rounded-2xl rounded-br-md'
    : msg.sender_role === 'admin'
      ? 'bg-[#3a1064] text-white border-[#ffd900] shadow-[0_2px_0_#ffd900] rounded-2xl rounded-bl-md'
      : 'bg-white text-[#3a1064] border-[#3a1064] shadow-[0_2px_0_#3a1064] rounded-2xl rounded-bl-md';
  const RoleIcon = msg.sender_role === 'admin' ? Shield : User;
  return (
    <div className={`flex ${side} gap-1.5`}>
      {!isMine && (
        <div className={`h-7 w-7 rounded-full border-2 border-[#3a1064] flex items-center justify-center shrink-0 ${
          msg.sender_role === 'admin' ? 'bg-[#ffd900] text-[#3a1064]' : 'bg-white text-[#3a1064]'
        }`}>
          <RoleIcon className="h-3.5 w-3.5" />
        </div>
      )}
      <div className={`max-w-[78%] border-2 px-3 py-1.5 ${bubble} ${msg._pending ? 'opacity-60' : ''}`}>
        {!isMine && (
          <div className="text-[10px] font-black uppercase opacity-80 tracking-wider mb-0.5 flex items-center gap-1">
            {msg.sender_name || (msg.sender_role === 'admin' ? 'Admin' : 'Buyer')}
            {msg.sender_role === 'admin' && (
              <span className="text-[8px] font-black uppercase px-1 py-0.5 rounded bg-[#ffd900] text-[#3a1064]">DEV</span>
            )}
          </div>
        )}
        <div className="text-sm font-bold whitespace-pre-wrap break-words leading-snug">{msg.body}</div>
        <div className={`text-[9px] font-black mt-0.5 ${isMine ? 'text-white/70 text-right' : 'opacity-60'}`}>
          {fmtTime(msg.created_at)}
        </div>
      </div>
    </div>
  );
}
